import { type SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useReactiveVar } from '@apollo/client/react';

import AppBar from '@mui/material/AppBar';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import SoftTypography from 'components/SoftTypography';
import { showSuiErrorSnackbar, showSuiSuccessSnackbar } from 'components/SuiSnackbar';

import { interpolateTemplate } from 'lib/i18n/dictionary';
import type { PlatformDictionary } from 'lib/i18n/types';
import {
  canHideReview,
  canRejectReport,
  fetchModerationReviews,
  fetchOpenReviewReports,
  formatModerationDate,
  getHiddenReasonKey,
  getReviewProfileLabel,
  hideReviewRequest,
  isModerationForbiddenError,
  keepsReviewOnTab,
  openReviewReportsCountVar,
  recordOpenReviewReportsCount,
  recordReviewModerationForbidden,
  rejectReviewReportRequest,
  resolveModerationSource,
  type ModeratedReview,
  type ModeratedReviewPage,
  type ReviewHiddenReason,
  type ReviewModerationFilters,
  type ReviewModerationTab,
} from 'review';

import { HideReviewDialog } from './hide-review-dialog';

const PAGE_SIZE = 20;
const TABS: ReviewModerationTab[] = ['reports', 'all', 'hidden'];
const STAR_OPTIONS = [5, 4, 3, 2, 1];
const ROW_COLUMNS = {
  xs: '1fr',
  lg: '170px 60px minmax(0, 1fr) 130px 150px 200px',
};

interface ReviewModerationBoardProps {
  accessToken?: string;
  locale: string;
  translations: PlatformDictionary['reviewsPage'];
}

export function ReviewModerationBoard({
  accessToken,
  locale,
  translations,
}: ReviewModerationBoardProps) {
  const openReportsCount = useReactiveVar(openReviewReportsCountVar);
  const [tab, setTab] = useState<ReviewModerationTab>('reports');
  const [filters, setFilters] = useState<ReviewModerationFilters>({ stars: null, profile: null });
  const [page, setPage] = useState<ModeratedReviewPage>({ items: [], total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isForbidden, setIsForbidden] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState('');
  const [busyReviewUuid, setBusyReviewUuid] = useState<string | null>(null);
  const [reviewToHide, setReviewToHide] = useState<ModeratedReview | null>(null);

  const loadPage = useCallback(
    async (offset: number) => {
      const source = resolveModerationSource(tab, filters);
      const nextPage =
        source.kind === 'openReports'
          ? await fetchOpenReviewReports(PAGE_SIZE, offset, accessToken)
          : await fetchModerationReviews(source.filter, PAGE_SIZE, offset, accessToken);

      if (source.kind === 'openReports') {
        recordOpenReviewReportsCount(nextPage.total);
      }

      return nextPage;
    },
    [accessToken, filters, tab]
  );

  const handleLoadError = useCallback(
    (error: unknown) => {
      if (isModerationForbiddenError(error)) {
        recordReviewModerationForbidden();
        setIsForbidden(true);
        return;
      }

      setLoadErrorMessage(error instanceof Error ? error.message : translations.loadFailed);
    },
    [translations.loadFailed]
  );

  useEffect(() => {
    let isCancelled = false;
    setIsLoading(true);
    setLoadErrorMessage('');

    loadPage(0)
      .then((nextPage) => {
        if (!isCancelled) {
          setPage(nextPage);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setPage({ items: [], total: 0 });
          handleLoadError(error);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [handleLoadError, loadPage]);

  useEffect(() => {
    if (loadErrorMessage) {
      showSuiErrorSnackbar(loadErrorMessage);
    }
  }, [loadErrorMessage]);

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    try {
      const nextPage = await loadPage(page.items.length);
      setPage((current) => ({
        items: [...current.items, ...nextPage.items],
        total: nextPage.total,
      }));
    } catch (error) {
      handleLoadError(error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const applyDecision = (decided: ModeratedReview, hadOpenReport: boolean) => {
    setPage((current) => {
      if (keepsReviewOnTab(tab, decided)) {
        return {
          ...current,
          items: current.items.map((item) => (item.uuid === decided.uuid ? decided : item)),
        };
      }

      return {
        items: current.items.filter((item) => item.uuid !== decided.uuid),
        total: Math.max(0, current.total - 1),
      };
    });

    if (hadOpenReport && openReportsCount !== null) {
      recordOpenReviewReportsCount(Math.max(0, openReportsCount - 1));
    }
  };

  const runDecision = async (
    review: ModeratedReview,
    request: () => Promise<ModeratedReview>,
    successMessage: string,
    failureMessage: string
  ) => {
    setBusyReviewUuid(review.uuid);
    try {
      const decided = await request();
      applyDecision(decided, canRejectReport(review));
      showSuiSuccessSnackbar(successMessage);
      return true;
    } catch (error) {
      if (isModerationForbiddenError(error)) {
        recordReviewModerationForbidden();
        setIsForbidden(true);
      } else {
        showSuiErrorSnackbar(error instanceof Error ? error.message : failureMessage);
      }
      return false;
    } finally {
      setBusyReviewUuid(null);
    }
  };

  const handleConfirmHide = async (reason: ReviewHiddenReason) => {
    if (!reviewToHide) {
      return;
    }

    const review = reviewToHide;
    const isDone = await runDecision(
      review,
      () => hideReviewRequest(review.uuid, reason, accessToken),
      translations.hidden,
      translations.hideFailed
    );
    if (isDone) {
      setReviewToHide(null);
    }
  };

  const handleRejectReport = (review: ModeratedReview) => {
    void runDecision(
      review,
      () => rejectReviewReportRequest(review.uuid, accessToken),
      translations.reportRejectedSuccess,
      translations.rejectFailed
    );
  };

  const handleTabChange = (_event: SyntheticEvent, nextIndex: number) => {
    setTab(TABS[nextIndex] ?? 'reports');
  };

  const handleFilterByProfile = (review: ModeratedReview) => {
    setFilters((current) => ({
      ...current,
      profile: {
        uuid: review.profileUuid,
        label: getReviewProfileLabel(review, translations.profileFallback),
      },
    }));
    if (tab === 'reports') {
      setTab('all');
    }
  };

  const getReportLabel = (review: ModeratedReview): string => {
    const report = review.report;
    if (!report) {
      return translations.reportNone;
    }

    if (report.state === 'OPEN') {
      return interpolateTemplate(translations.reportOpen, {
        date: formatModerationDate(report.createdAt, locale),
      });
    }

    return interpolateTemplate(
      report.state === 'REJECTED' ? translations.reportRejected : translations.reportUpheld,
      { date: formatModerationDate(report.decidedAt, locale) }
    );
  };

  if (isForbidden) {
    return (
      <Card data-testid="reviews-forbidden">
        <SoftBox p={3}>
          <SoftTypography variant="button" color="text" fontWeight="regular">
            {translations.forbidden}
          </SoftTypography>
        </SoftBox>
      </Card>
    );
  }

  const areFiltersDisabled = tab === 'reports';

  return (
    <SoftBox display="flex" flexDirection="column" gap={2}>
      <SoftBox
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
      >
        <SoftBox minWidth={{ xs: '100%', md: 420 }}>
          <AppBar position="static">
            <Tabs
              value={TABS.indexOf(tab)}
              onChange={handleTabChange}
              sx={{ '&.MuiTabs-root': { backgroundColor: 'transparent !important' } }}
            >
              <Tab
                data-testid="reviews-tab-reports"
                label={interpolateTemplate(translations.tabReports, {
                  count: String(openReportsCount ?? 0),
                })}
              />
              <Tab data-testid="reviews-tab-all" label={translations.tabAll} />
              <Tab data-testid="reviews-tab-hidden" label={translations.tabHidden} />
            </Tabs>
          </AppBar>
        </SoftBox>
        <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {filters.profile ? (
            <Chip
              data-testid="reviews-profile-filter"
              label={interpolateTemplate(translations.profileFilter, {
                profile: filters.profile.label,
              })}
              onDelete={
                areFiltersDisabled
                  ? undefined
                  : () => setFilters((current) => ({ ...current, profile: null }))
              }
              disabled={areFiltersDisabled}
              title={translations.clearProfileFilter}
            />
          ) : null}
          <FormControl sx={{ minWidth: 160 }}>
            <Select
              data-testid="reviews-stars-filter"
              displayEmpty
              input={<SoftInput inputProps={{ 'aria-label': translations.starsLabel }} />}
              value={filters.stars === null ? '' : String(filters.stars)}
              disabled={areFiltersDisabled}
              onChange={(event) => {
                const value = String(event.target.value);
                setFilters((current) => ({
                  ...current,
                  stars: value === '' ? null : Number(value),
                }));
              }}
            >
              <MenuItem value="">{translations.starsAll}</MenuItem>
              {STAR_OPTIONS.map((stars) => (
                <MenuItem key={stars} value={String(stars)}>
                  {interpolateTemplate(translations.starsOption, { stars: String(stars) })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </SoftBox>
      </SoftBox>
      {areFiltersDisabled ? (
        <SoftTypography variant="caption" color="text">
          {translations.filtersUnavailableForReports}
        </SoftTypography>
      ) : null}

      <Card data-testid="reviews-table">
        <SoftBox
          display={{ xs: 'none', lg: 'grid' }}
          gridTemplateColumns={ROW_COLUMNS.lg}
          gap={1.5}
          px={2}
          py={1.25}
          sx={{ borderBottom: '1px solid #e9ecef' }}
        >
          {[
            translations.columnProfile,
            translations.columnStars,
            translations.columnReview,
            translations.columnChannel,
            translations.columnReport,
            translations.columnActions,
          ].map((column) => (
            <SoftTypography key={column} variant="caption" fontWeight="bold" color="secondary">
              {column}
            </SoftTypography>
          ))}
        </SoftBox>

        {isLoading ? (
          <SoftBox py={6} display="flex" flexDirection="column" alignItems="center" gap={1.5}>
            <CircularProgress color="info" />
            <SoftTypography variant="button" color="text">
              {translations.loading}
            </SoftTypography>
          </SoftBox>
        ) : page.items.length === 0 ? (
          <SoftBox p={4} textAlign="center">
            <SoftTypography variant="button" color="text" data-testid="reviews-empty">
              {tab === 'reports' ? translations.emptyReports : translations.emptyReviews}
            </SoftTypography>
          </SoftBox>
        ) : (
          page.items.map((review) => {
            const isBusy = busyReviewUuid === review.uuid;
            const profileLabel = getReviewProfileLabel(review, translations.profileFallback);

            return (
              <SoftBox
                key={review.uuid}
                data-testid="review-row"
                display="grid"
                gridTemplateColumns={ROW_COLUMNS}
                gap={1.5}
                px={2}
                py={1.75}
                alignItems="start"
                sx={{ borderBottom: '1px solid #f0f2f5' }}
              >
                <SoftBox>
                  <SoftTypography
                    component="button"
                    type="button"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    title={translations.filterByProfile}
                    onClick={() => handleFilterByProfile(review)}
                    sx={{
                      background: 'none',
                      border: 0,
                      p: 0,
                      cursor: 'pointer',
                      textAlign: 'left',
                      lineHeight: 1.5,
                    }}
                  >
                    {profileLabel}
                  </SoftTypography>
                </SoftBox>
                <SoftTypography variant="button" fontWeight="bold">
                  {review.stars}
                </SoftTypography>
                <SoftBox>
                  <SoftTypography variant="button" fontWeight="regular" sx={{ lineHeight: 1.5 }}>
                    <strong>{review.authorName}</strong> · {review.text}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" component="div" pt={0.5}>
                    {interpolateTemplate(translations.replyLabel, {
                      reply: review.reply?.text ?? translations.replyNone,
                    })}
                  </SoftTypography>
                </SoftBox>
                <SoftTypography variant="button" color="text" fontWeight="regular">
                  {review.channel === 'LINK' ? translations.channelLink : translations.channelEmail}
                </SoftTypography>
                <SoftBox>
                  <SoftTypography
                    variant="button"
                    fontWeight="regular"
                    color={review.report?.state === 'OPEN' ? 'warning' : 'text'}
                  >
                    {getReportLabel(review)}
                  </SoftTypography>
                  {review.report?.message ? (
                    <SoftTypography variant="caption" color="text" component="div" pt={0.5}>
                      {interpolateTemplate(translations.reportMessage, {
                        message: review.report.message,
                      })}
                    </SoftTypography>
                  ) : null}
                </SoftBox>
                <SoftBox display="flex" gap={0.75} flexWrap="wrap" alignItems="center">
                  {review.hiddenAt && review.hiddenReason ? (
                    <SoftTypography variant="caption" color="text" data-testid="review-hidden">
                      {interpolateTemplate(translations.hiddenStatus, {
                        reason: translations[getHiddenReasonKey(review.hiddenReason)],
                      })}
                    </SoftTypography>
                  ) : null}
                  {canHideReview(review) ? (
                    <SoftButton
                      size="small"
                      variant="gradient"
                      color="dark"
                      disabled={isBusy}
                      onClick={() => setReviewToHide(review)}
                      data-testid="review-hide"
                    >
                      {translations.hideAction}
                    </SoftButton>
                  ) : null}
                  {canRejectReport(review) ? (
                    <SoftButton
                      size="small"
                      variant="outlined"
                      color="secondary"
                      disabled={isBusy}
                      onClick={() => handleRejectReport(review)}
                      data-testid="review-reject-report"
                    >
                      {translations.rejectReportAction}
                    </SoftButton>
                  ) : null}
                </SoftBox>
              </SoftBox>
            );
          })
        )}
      </Card>

      {!isLoading && page.items.length < page.total ? (
        <SoftBox display="flex" justifyContent="center">
          <SoftButton
            variant="outlined"
            color="info"
            size="small"
            disabled={isLoadingMore}
            onClick={() => {
              void handleLoadMore();
            }}
          >
            {translations.loadMore}
          </SoftButton>
        </SoftBox>
      ) : null}

      <HideReviewDialog
        review={reviewToHide}
        isSubmitting={Boolean(reviewToHide) && busyReviewUuid === reviewToHide?.uuid}
        translations={translations}
        onCancel={() => setReviewToHide(null)}
        onConfirm={(reason) => {
          void handleConfirmHide(reason);
        }}
      />
    </SoftBox>
  );
}
